import React, { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import { useLanguage } from '../lib/LanguageContext';
import { findWork, TERRIBLE_CHILDREN_INTRO } from '../lib/worksData';

const UI = {
  en: {
    home: 'Home',
    artwork: 'Artwork',
    about: 'About the work',
    details: 'Details',
    medium: 'Medium',
    dimensions: 'Dimensions',
    materials: 'Materials',
    series: 'Series',
    seriesContext: 'Series context',
  },
  ro: {
    home: 'Acasă',
    artwork: 'Lucrare',
    about: 'Despre lucrare',
    details: 'Detalii',
    medium: 'Tehnică',
    dimensions: 'Dimensiuni',
    materials: 'Materiale',
    series: 'Serie',
    seriesContext: 'Contextul seriei',
  },
};

const WorkDetailPage = () => {
  const { id } = useParams();
  const { language } = useLanguage();
  const labels = UI[language] || UI.en;
  const work = findWork(id);

  useEffect(() => {
    if (!work) return undefined;
    const previousTitle = document.title;
    document.title = `${work.title} — DIMIRXSON`;
    return () => { document.title = previousTitle; };
  }, [work]);

  if (!work) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-bone text-charcoal" data-testid="work-detail-page">
      <Header />

      <main>
        <section className="px-6 pb-10 pt-32 md:px-12 md:pb-14 md:pt-40 lg:px-16">
          <Reveal>
            <nav className="flex items-center gap-2 text-[10px] uppercase tracking-eyebrow text-charcoal/50 md:text-[11px]">
              <Link to="/" className="transition-opacity hover:opacity-60">{labels.home}</Link>
              <span>/</span>
              <span className="max-w-[42vw] truncate text-charcoal/80">{work.title}</span>
            </nav>
          </Reveal>

          <div className="mt-10 grid grid-cols-12 gap-x-6 gap-y-7 border-t border-charcoal pt-7 md:mt-14 md:pt-9">
            <Reveal className="col-span-12 lg:col-span-10" delay={0.05}>
              <span className="text-[11px] uppercase tracking-eyebrow text-charcoal/55">
                {labels.artwork}
              </span>
              <h1 className="mt-6 max-w-[1500px] font-sans text-5xl font-extrabold leading-[0.91] tracking-[-0.02em] sm:text-6xl md:text-7xl lg:text-[7.2vw]" data-testid="work-title">
                {work.title}
              </h1>
            </Reveal>
            {work.series && (
              <Reveal className="col-span-12 self-end lg:col-span-2" delay={0.12}>
                <p className="text-[11px] uppercase leading-relaxed tracking-eyebrow text-charcoal/55">
                  {labels.series}<br /><span className="text-charcoal">{work.series}</span>
                </p>
              </Reveal>
            )}
          </div>
        </section>

        <section className="px-6 pb-16 md:px-12 md:pb-24 lg:px-16">
          <Reveal>
            <div className="relative flex min-h-[55vh] w-full items-center justify-center overflow-hidden bg-white px-3 py-3 sm:px-8 sm:py-8 md:min-h-[72vh] lg:min-h-[82vh]">
              <motion.img
                src={work.image}
                alt={work.title}
                initial={{ opacity: 0, scale: 1.025 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
                className="max-h-[82vh] w-full object-contain"
                loading="eager"
                decoding="async"
              />
            </div>
          </Reveal>
        </section>

        <section className="border-t border-charcoal px-6 py-16 md:px-12 md:py-24 lg:px-16 lg:py-28">
          <div className="grid grid-cols-12 gap-x-6 gap-y-12">
            <Reveal className="col-span-12 md:col-span-3">
              <h2 className="font-sans text-3xl font-extrabold md:text-4xl">{labels.about}</h2>
            </Reveal>

            <div className="col-span-12 md:col-span-7 md:col-start-5">
              {work.description.map((paragraph, paragraphIndex) => (
                <Reveal key={paragraph} delay={0.06 * paragraphIndex}>
                  <p className={`${paragraphIndex ? 'mt-7 text-lg text-charcoal/75 md:text-xl' : 'text-xl text-charcoal md:text-2xl'} whitespace-pre-wrap font-bold leading-relaxed`}>
                    {paragraph}
                  </p>
                </Reveal>
              ))}
            </div>

            {(work.medium || work.dimensions || work.materials || work.series) && (
              <Reveal className="col-span-12 border-t border-charcoal/25 pt-6 md:col-span-3 md:col-start-10 md:border-t-0 md:pt-1" delay={0.14}>
                <h3 className="text-[11px] uppercase tracking-eyebrow text-charcoal/45">{labels.details}</h3>
                <dl className="mt-6 space-y-5 text-sm leading-relaxed">
                  {work.medium && <Detail label={labels.medium} value={work.medium} />}
                  {work.dimensions && <Detail label={labels.dimensions} value={work.dimensions} />}
                  {work.materials && <Detail label={labels.materials} value={work.materials} />}
                  {work.series && <Detail label={labels.series} value={work.series} />}
                </dl>
              </Reveal>
            )}
          </div>
        </section>

        {work.series === 'The Terrible Children' && (
          <section className="bg-charcoal px-6 py-16 text-bone md:px-12 md:py-24 lg:px-16 lg:py-28">
            <div className="grid grid-cols-12 gap-x-6 gap-y-12">
              <Reveal className="col-span-12 md:col-span-4">
                <span className="text-[11px] uppercase tracking-eyebrow text-bone/50">{labels.seriesContext}</span>
                <h2 className="mt-6 font-sans text-4xl font-extrabold leading-none sm:text-5xl md:text-6xl">The Terrible<br />Children</h2>
              </Reveal>
              <div className="col-span-12 md:col-span-7 md:col-start-6">
                {TERRIBLE_CHILDREN_INTRO.map((paragraph, paragraphIndex) => (
                  <Reveal key={paragraph} delay={0.06 * paragraphIndex}>
                    <p className={`${paragraphIndex ? 'mt-6 text-bone/70' : 'text-bone'} text-lg font-bold leading-relaxed md:text-xl`}>
                      {paragraph}
                    </p>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

      </main>

      <Footer />
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div>
    <dt className="text-[10px] uppercase tracking-eyebrow text-charcoal/45">{label}</dt>
    <dd className="mt-1.5 font-bold text-charcoal/80">{value}</dd>
  </div>
);

export default WorkDetailPage;
